// Owner / Leadership Dashboard (phase 1) — pure calculations.
// Everything here is a plain function of already-fetched data (jobs, shifts,
// maintenance rows and the per-device transitions / downtime derived from one
// datapoints response) so the rules can be unit tested without React or
// network. Signal handling, downtime detection and interval maths are reused
// from the Reports v2 data layer rather than re-implemented.

import {
    bucketCountsByDay,
    clipIntervals,
    detectDowntime,
    deviceTransitions,
    formatDuration,
    jobLastStepDeviceId,
    mergeIntervals,
    shiftWindowOnDate,
} from "../report/phase1/reportData";

// ---- Thresholds ------------------------------------------------------------

// A running job is "behind" when it has made less than this share of the
// pieces it should have made by now.
export const BEHIND_TARGET_RATIO = 0.9;
// A machine that has been down (and still is) for longer than this alerts.
export const LONG_DOWNTIME_SECONDS = 30 * 60;
// detectDowntime closes an ongoing interval at the end of the range it was
// given; anything ending this close to "now" is treated as still going.
export const ONGOING_TOLERANCE_SECONDS = 60;

export const SEVERITY = { HIGH: "High", MEDIUM: "Medium" };
const SEVERITY_RANK = { [SEVERITY.HIGH]: 0, [SEVERITY.MEDIUM]: 1 };

export const ALERT_TYPE = {
    BEHIND_TARGET: "behind-target",
    LONG_DOWNTIME: "long-downtime",
    UNDER_MAINTENANCE: "under-maintenance",
};

// ---- Small helpers ---------------------------------------------------------

const toMs = (v) => new Date(v).getTime();
const totalLength = (intervals) => intervals.reduce((sum, [s, e]) => sum + (e - s), 0);

export function startOfDay(ms) {
    const d = new Date(ms);
    d.setHours(0, 0, 0, 0);
    return d.getTime();
}

// Same clock time on the previous calendar day (DST-safe, unlike - 86400000),
// so "today so far" is compared with the same elapsed span of yesterday.
export function sameTimeYesterday(nowMs) {
    const d = new Date(nowMs);
    d.setDate(d.getDate() - 1);
    return Math.min(d.getTime(), startOfDay(nowMs));
}

// Local midnight after the day holding `ms` (DST-safe, unlike + 86400000).
function nextMidnight(ms) {
    const d = new Date(startOfDay(ms));
    d.setDate(d.getDate() + 1);
    return d.getTime();
}

// Local calendar days touching [startMs, endMs], starting one day early so an
// overnight window that opened the previous evening is still picked up.
function daysCovering(startMs, endMs) {
    const days = [];
    const d = new Date(startOfDay(startMs));
    d.setDate(d.getDate() - 1);
    while (d.getTime() <= endMs) {
        days.push(new Date(d));
        d.setDate(d.getDate() + 1);
    }
    return days;
}

// schedule_attributes is JSON; older rows can arrive as a string.
function attrsOf(schedule) {
    const attrs = schedule?.schedule_attributes;
    if (typeof attrs !== "string") return attrs || {};
    try {
        return JSON.parse(attrs) || {};
    } catch {
        return {};
    }
}

// Durations past a day read better as days than as "150h 0m".
export function formatLongDuration(seconds) {
    if (seconds < 86400) return formatDuration(seconds);
    const d = Math.floor(seconds / 86400);
    const h = Math.floor((seconds % 86400) / 3600);
    return h ? `${d}d ${h}h` : `${d}d`;
}

// ---- Per-device inputs (from the single datapoints response) ---------------

// Production transitions per device id, from one datapoints response.
export function buildTransitionsByDevice(devices, datapoints, types) {
    const out = {};
    (devices || []).forEach((device) => {
        out[device.id] = datapoints ? deviceTransitions(device, datapoints, types) : [];
    });
    return out;
}

// Detected downtime intervals per device id over [startSec, endSec].
export function buildDowntimeByDevice(devices, datapoints, types, startSec, endSec) {
    const out = {};
    (devices || []).forEach((device) => {
        out[device.id] = datapoints
            ? detectDowntime(device, datapoints, types, startSec, endSec)
            : [];
    });
    return out;
}

// ---- Production ------------------------------------------------------------

// A job runs from start_time to end_time; when it is linked to a shift it only
// runs during that shift's hours on each of those days. Returns ms windows ∩
// [startMs, endMs].
export function jobRunWindows(job, startMs, endMs) {
    const js = Math.max(toMs(job.start_time), startMs);
    const je = Math.min(toMs(job.end_time), endMs);
    if (!(je > js)) return [];
    if (!job.shift) return [[js, je]];
    const daily = daysCovering(js, je)
        .map((day) => shiftWindowOnDate(job.shift, day))
        .filter(Boolean);
    return clipIntervals([[js, je]], daily);
}

// Keys of the transitions one job produced in the period: only its LAST-STEP
// machine counts, so a part routed through several machines is counted once.
function jobTransitionKeys(job, transitionsByDevice, startMs, endMs) {
    const deviceId = jobLastStepDeviceId(job);
    const transitions = (deviceId && transitionsByDevice?.[deviceId]) || [];
    if (!transitions.length) return [];
    const windows = jobRunWindows(job, startMs, endMs);
    if (!windows.length) return [];
    return transitions
        .filter((t) => {
            const ms = t.timestamp * 1000;
            // Half-open so a piece on a day boundary lands in exactly one day.
            return windows.some(([s, e]) => ms >= s && ms < e);
        })
        .map((t) => `${deviceId}@${t.timestamp}`);
}

// Rule 1 (one job): pieces counted on the job's last-step machine inside its run window ∩ the period.
export function jobProduction(job, transitionsByDevice, startMs, endMs) {
    return jobTransitionKeys(job, transitionsByDevice, startMs, endMs).length;
}

// Rule 1: Σ over jobs of last-step pieces inside each job's run window ∩ the
// period. A piece claimed by two overlapping jobs on the same machine counts
// once. Production on machines with no job running is NOT counted.
export function totalProduction(jobs, transitionsByDevice, startMs, endMs) {
    const seen = new Set();
    (jobs || []).forEach((job) =>
        jobTransitionKeys(job, transitionsByDevice, startMs, endMs).forEach((k) =>
            seen.add(k),
        ),
    );
    return seen.size;
}

// Share of one job's target inside [startMs, endMs], spread over the same run
// windows its production is counted in (only its shift hours when it is
// shift-linked), so target and production run on one clock. Unrounded; 0 when
// the job has no target or no run time at all.
export function jobTarget(job, startMs, endMs) {
    if (job?.target_units == null) return 0;
    const runLength = totalLength(jobRunWindows(job, -Infinity, Infinity));
    if (!(runLength > 0)) return 0;
    return job.target_units * (totalLength(jobRunWindows(job, startMs, endMs)) / runLength);
}

// Rule 2: target = Σ jobTarget(job, period) over jobs that have target_units.
export function periodTarget(jobs, startMs, endMs) {
    return Math.round(
        (jobs || [])
            .filter((job) => job.target_units != null)
            .reduce((sum, job) => sum + jobTarget(job, startMs, endMs), 0),
    );
}

// Rule 3: achievement % = production of targeted jobs ÷ their target × 100;
// untargeted jobs are left out so they cannot inflate it. pct null when target is 0.
export function achievement(jobs, transitionsByDevice, startMs, endMs) {
    const targeted = (jobs || []).filter((job) => job.target_units != null);
    const actual = totalProduction(targeted, transitionsByDevice, startMs, endMs);
    const target = periodTarget(targeted, startMs, endMs);
    return { actual, target, pct: target > 0 ? (actual / target) * 100 : null };
}

// Rule 5: Total Production per local calendar day from startMs's day to today.
export function productionTrend(jobs, transitionsByDevice, startMs, nowMs) {
    // bucketCountsByDay supplies the day scaffold and labels; counts are ours.
    return bucketCountsByDay([], startMs, nowMs).map((day) => {
        const end = Math.min(nextMidnight(day.start), nowMs);
        return {
            key: day.key,
            label: day.label,
            start: day.start,
            end,
            count: totalProduction(jobs, transitionsByDevice, day.start, end),
        };
    });
}

// ---- Downtime --------------------------------------------------------------

// Every active shift's window on every day touching the period, in unix
// seconds, merged and clipped to the period.
export function shiftWindowsForPeriod(shifts, startMs, endMs) {
    const windows = [];
    daysCovering(startMs, endMs).forEach((day) =>
        (shifts || []).forEach((shift) => {
            const w = shiftWindowOnDate(shift, day);
            if (w) windows.push([w[0] / 1000, w[1] / 1000]);
        }),
    );
    return clipIntervals(windows, [[startMs / 1000, endMs / 1000]]);
}

// Rule 4: per device, detected downtime clipped to shift hours and merged (no
// overlap double counts), summed across devices. Seconds; null = no shifts.
export function downtimeSeconds(downtimeByDevice, shifts, startMs, endMs) {
    if (!shifts?.length) return null;
    const windows = shiftWindowsForPeriod(shifts, startMs, endMs);
    return Object.values(downtimeByDevice || {}).reduce(
        (sum, events) =>
            sum + totalLength(clipIntervals(events.map((e) => [e.start, e.end]), windows)),
        0,
    );
}

// ---- Maintenance -----------------------------------------------------------

function scheduleTypes(types) {
    const st = types?.maintenance_schedules?.schedule_types;
    return { SCHEDULED: st?.SCHEDULED ?? 2, ADHOC: st?.ADHOC ?? 1 };
}

// Defensive repeat of the query filter: never count archived or inactive rows.
const isLiveSchedule = (s) => s.deleted_at == null && s.is_active !== false;

// Rule 6: upcoming = SCHEDULED with from > now (soonest first); under
// maintenance now = ADHOC with from set and no "to".
export function maintenanceSummary(schedules, types, nowMs) {
    const { SCHEDULED, ADHOC } = scheduleTypes(types);
    const live = (schedules || []).filter(isLiveSchedule);
    const upcoming = live
        .filter((s) => {
            const { from } = attrsOf(s);
            return s.schedule_type === SCHEDULED && from && toMs(from) > nowMs;
        })
        .sort((a, b) => toMs(attrsOf(a).from) - toMs(attrsOf(b).from));
    const underMaintenance = live.filter((s) => {
        const { from, to } = attrsOf(s);
        return s.schedule_type === ADHOC && !!from && !to;
    });
    return { upcoming, underMaintenance };
}

// ---- Business alerts -------------------------------------------------------

// Behind Target (High): a job running now with a target whose pieces so far
// are under BEHIND_TARGET_RATIO of its prorated target so far. Telemetry only
// reaches back to dataStartMs, so an older run is judged on the span we see.
export function behindTargetAlerts(jobs, transitionsByDevice, nowMs, dataStartMs) {
    const alerts = [];
    (jobs || []).forEach((job) => {
        if (job.target_units == null) return;
        if (toMs(job.start_time) > nowMs || toMs(job.end_time) < nowMs) return;
        const from = Math.max(toMs(job.start_time), dataStartMs ?? -Infinity);
        const expected = Math.round(jobTarget(job, from, nowMs));
        if (expected < 1) return;
        const actual = jobProduction(job, transitionsByDevice, from, nowMs);
        if (actual >= BEHIND_TARGET_RATIO * expected) return;
        alerts.push({
            key: `${ALERT_TYPE.BEHIND_TARGET}-${job.id}`,
            type: ALERT_TYPE.BEHIND_TARGET,
            severity: SEVERITY.HIGH,
            time: nowMs,
            text: `${job.name} behind target — ${actual} of ${expected} expected so far`,
        });
    });
    return alerts;
}

// Long Downtime (High): a machine whose latest downtime is still ongoing at
// now and has lasted longer than LONG_DOWNTIME_SECONDS. Like rule 4, only shift
// hours count: no alert while off shift (nights, days off), the duration skips
// off-shift time, and a plant with no shifts has no basis to alert on.
export function longDowntimeAlerts(devices, downtimeByDevice, shifts, nowSec, dataStartSec) {
    if (!shifts?.length) return [];
    const alerts = [];
    (devices || []).forEach((device) => {
        // Merged so a stop that rolls into telemetry silence reads as one stretch.
        const merged = mergeIntervals(
            (downtimeByDevice?.[device.id] || []).map((e) => [e.start, e.end]),
        );
        const last = merged[merged.length - 1];
        if (!last) return;
        const [start, end] = last;
        if (end < nowSec - ONGOING_TOLERANCE_SECONDS) return;
        const windows = shiftWindowsForPeriod(shifts, start * 1000, nowSec * 1000);
        // Off shift right now: nobody expects the machine to be running.
        if (!windows.some(([s, e]) => nowSec > s && nowSec <= e)) return;
        const lasted = totalLength(clipIntervals([[start, nowSec]], windows));
        if (lasted <= LONG_DOWNTIME_SECONDS) return;
        // Down since before the data we fetched: we only know a lower bound.
        const capped = dataStartSec != null && start <= dataStartSec;
        alerts.push({
            key: `${ALERT_TYPE.LONG_DOWNTIME}-${device.id}`,
            type: ALERT_TYPE.LONG_DOWNTIME,
            severity: SEVERITY.HIGH,
            time: start * 1000,
            text: `Machine ${device.name} down for ${capped ? "over " : ""}${formatLongDuration(lasted)}`,
        });
    });
    return alerts;
}

// Machine Under Maintenance (Medium): each open ADHOC maintenance record.
export function maintenanceAlerts(underMaintenance) {
    return (underMaintenance || []).map((s) => {
        const attrs = attrsOf(s);
        const detail = [attrs.maintenance_type_name, attrs.reason].filter(Boolean).join(" — ");
        const name = s.device?.name || s.device_id;
        return {
            key: `${ALERT_TYPE.UNDER_MAINTENANCE}-${s.id}`,
            type: ALERT_TYPE.UNDER_MAINTENANCE,
            severity: SEVERITY.MEDIUM,
            time: toMs(attrs.from),
            text: `Machine ${name} under maintenance${detail ? ` — ${detail}` : ""}`,
        };
    });
}

// Rule 7: all business alerts, High before Medium, then most recent first.
// transitionsByDevice / downtimeByDevice of null mean "could not be loaded":
// those alert families are skipped rather than raised from empty data.
export function businessAlerts({
    jobs,
    devices,
    shifts,
    transitionsByDevice,
    downtimeByDevice,
    underMaintenance,
    nowMs,
    dataStartMs,
}) {
    return [
        ...(transitionsByDevice
            ? behindTargetAlerts(jobs, transitionsByDevice, nowMs, dataStartMs)
            : []),
        ...(downtimeByDevice
            ? longDowntimeAlerts(
                  devices,
                  downtimeByDevice,
                  shifts,
                  Math.floor(nowMs / 1000),
                  dataStartMs != null ? Math.floor(dataStartMs / 1000) : null,
              )
            : []),
        ...maintenanceAlerts(underMaintenance),
    ].sort(
        (a, b) =>
            SEVERITY_RANK[a.severity] - SEVERITY_RANK[b.severity] ||
            (b.time || 0) - (a.time || 0),
    );
}

// ---- Presentation helpers --------------------------------------------------

// Day-over-day change. lowerIsBetter flips the colour (downtime falling is good).
// Returns null when there is no basis to compare (missing value or zero base).
export function changeVs(today, yesterday, { lowerIsBetter = false } = {}) {
    if (today == null || yesterday == null) return null;
    if (yesterday === 0) {
        return today === 0 ? { pct: 0, direction: "flat", good: true } : null;
    }
    const pct = ((today - yesterday) / yesterday) * 100;
    const direction = pct > 0 ? "up" : pct < 0 ? "down" : "flat";
    const good = direction === "flat" || (direction === "down") === lowerIsBetter;
    return { pct, direction, good };
}
